import { useEffect, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";

import { buildNavigation, canOpen, pagesNotInRole, merchantPages } from "../navigation.ts";
import type { Session } from "../session.ts";
import { HeaderKit } from "./HeaderKit.tsx";

/**
 * The merchant portal's chrome, built to the rail `22 WP21 Merchant Portal.dc.html` draws.
 *
 * **Its rail is darker than every other console's, in both themes, and that is the design.** Where
 * the tenant admin and finance files write `background: 'var(--nav)'` and
 * `borderRight: '1px solid var(--navEdge)'` — which resolve to `#0C111D` on `transparent` in light
 * and `#070A10` on `#232A37` in dark — this file writes `background: '#070A10'` and
 * `borderRight: '1px solid #232A37'` as literals. So the platform rail is the dark values at every
 * width in every theme, which reads as a different surface from the tenant consoles beside it. That
 * is a deliberate signal on a control plane rather than an oversight in the design file, and it is
 * why `styles.css` here declares colours of its own where its sibling declares none — see
 * `tests/stylesheet-tokens.test.ts`, which holds every one of them to appearing in a design file.
 *
 * Otherwise it is the tenant admin console's shell, which is that file's shell: menu groups fold
 * with a rotating `expand_more` chevron and the fold is remembered; a folded group still shows its
 * items when the rail is collapsed, because a 76px rail has no group headings to fold under —
 * `showItems: open || !full` in the design's own words; the head of the rail is a mark, a name and
 * the collapse control; and there is no merchant block at the foot, because this file puts the
 * merchant in the account menu only.
 *
 * The geometry is the one Q-08 settled and this file agrees with: 256px full, 76px collapsed between
 * 640 and 1239, out of the layout below 640 with a drawer on demand at 260px.
 *
 * What the design draws and this does not is the per-item count badge — the violet pill reading `9`
 * beside "Risk and fraud" and `7` beside "Dead letters". Those are, in the design's own help tip,
 * "items that need a person", and **no listing on this platform publishes a total**: R-04 measured
 * 0 of 70 non-internal collection reads. On this console it is worse than that, because fifteen of
 * the sixteen pages have no listing at all — a badge would be a count of nothing, beside a page an
 * merchant cannot open. That is **Q-21**, unchanged: the question is what the badge counts, and it
 * is the same question on every console — this one only makes the answer more obviously unavailable.
 */

/**
 * The help popover's tips, after the page's own description.
 *
 * The design passes three: the page's own description, "Cross-tenant actions are recorded in both
 * tenants' audit logs", and "Counts in the menu are items that need a person". The third describes
 * the count badges this console does not draw, so repeating it would point a merchant at something
 * that is not there. Two of three, and Q-20 records the third.
 */
const SHELL_TIPS = ["Cross-tenant actions are recorded in both tenants' audit logs."] as const;

/**
 * The two widths the rail's own control has to know about, as media queries rather than numbers.
 *
 * The design measures its frame and branches on `w >= 640` and `w < 1240`. A console is the page
 * rather than a frame inside one, so the stylesheet owns those thresholds — but the collapse button
 * cannot: it has to say "Collapse" or "Expand", and which of those is true depends on the width.
 * Reading it back from the same thresholds the stylesheet uses keeps one statement of each number.
 *
 * `matchMedia` is absent in some test environments; the fallback is the widest layout, which is the
 * one a server-rendered or unmeasured first paint should assume.
 */
function useMediaQuery(query: string, fallback: boolean): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? fallback
      : window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const list = window.matchMedia(query);
    const onChange = (): void => {
      setMatches(list.matches);
    };
    onChange();
    list.addEventListener("change", onChange);
    return (): void => {
      list.removeEventListener("change", onChange);
    };
  }, [query]);

  return matches;
}

export interface ShellProps {
  readonly session: Session;
  readonly currentPageId: string;
  readonly onNavigate: (pageId: string) => void;
  /** Passed through to the header, whose notification bell reads the in-app inbox. */
  readonly transport: Transport;
  /** What the account menu's last row does. Absent on the development path, where there is no
      session to end — the row is then inert rather than hidden, because the design's account menu
      has five rows and removing one would be removing part of the header kit. */
  readonly onSignOut?: () => void;
  readonly children: ReactNode;
}

export function Shell({
  session,
  currentPageId,
  onNavigate,
  transport,
  onSignOut,
  children,
}: ShellProps): ReactNode {
  const [navOpen, setNavOpen] = useState(false);
  /**
   * Which groups are folded.
   *
   * The design's own `closed` map, keyed by group name and defaulting to open, and the fold is
   * remembered across navigation because the design remembers it: a page change sets `page` and
   * leaves `closed` alone.
   */
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  /**
   * The rail's width, where the administrator has chosen one.
   *
   * `undefined` means "follow the width", which is the design's own `sideCollapsed: null`. A chosen
   * value overrides the threshold in both directions: an administrator can collapse a 1440 rail and
   * expand a 1024 one, which is what that file's `toggleSide` does.
   */
  const [railCollapsed, setRailCollapsed] = useState<boolean | undefined>(undefined);

  const wide = useMediaQuery("(min-width: 1240px)", true);
  const drawerWidth = useMediaQuery("(max-width: 639px)", false);
  /** Full: labels, group headings and the product name are on screen. The drawer is always full. */
  const full = drawerWidth ? true : (railCollapsed ?? !wide) === false;

  const sections = buildNavigation(session.scopes);
  const hidden = pagesNotInRole(session.scopes);
  const page = merchantPages.find((candidate) => candidate.id === currentPageId);
  const reachable = canOpen(currentPageId, session.scopes);

  const toggleGroup = (group: string): void => {
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <div
      className={[
        "shell",
        session.crossTenant ? "shell--cross-tenant" : "",
        // Only when chosen. With no choice the stylesheet's own thresholds decide, which is the
        // design's `sideCollapsed: null`.
        railCollapsed === true ? "shell--rail" : "",
        railCollapsed === false ? "shell--full" : "",
      ]
        .filter((part) => part.length > 0)
        .join(" ")}
    >
      <nav className={navOpen ? "shell__nav shell__nav--open" : "shell__nav"} aria-label="Merchant">
        {/*
          The head of the rail: the design's `storefront` mark on white, the word
          "Merchant" beside it while the rail is full, and the collapse control at the far end. Below
          1240 the name goes and the row stacks, which is what `logoRow`'s
          `flexDirection: full ? 'row' : 'column'` does.
        */}
        <div className="shell__brand">
          <span aria-hidden="true" className="shell__brand-mark">
            storefront
          </span>
          <span className="shell__brand-name">Merchant</span>
          {/*
            The design's own collapse control, with its two behaviours: in the drawer it closes the
            drawer, and anywhere else it chooses the rail's width and overrides the threshold. The
            label and the glyph follow the rail's *current* state, which is why the width is read
            back from the same media queries the stylesheet branches on rather than guessed.
          */}
          <button
            type="button"
            className="shell__collapse"
            aria-label={full ? "Collapse menu" : "Expand menu"}
            onClick={() => {
              if (drawerWidth) setNavOpen(false);
              else setRailCollapsed(full);
            }}
          >
            <span aria-hidden="true" className="shell__collapse-glyph">
              {full ? "left_panel_close" : "left_panel_open"}
            </span>
          </button>
        </div>

        {sections.length === 0 ? (
          // Not an empty rail. An administrator with no reachable pages has been given an account
          // and no permissions, and telling them so is the only useful thing this screen can do.
          <p className="shell__nav-empty">
            Your account has no platform permissions yet. Ask whoever administers your roles.
          </p>
        ) : (
          sections.map((section) => {
            const open = !folded.has(section.section);
            return (
              <div key={section.section} className="shell__section">
                <button
                  type="button"
                  className="shell__section-title"
                  aria-expanded={open}
                  onClick={() => toggleGroup(section.section)}
                >
                  <span className="shell__section-name">{section.section}</span>
                  <span
                    aria-hidden="true"
                    className={
                      open
                        ? "shell__section-chevron"
                        : "shell__section-chevron shell__section-chevron--folded"
                    }
                  >
                    expand_more
                  </span>
                </button>
                {/*
                  `open || !full` is the design's own condition, and the `!full` half is the part
                  worth keeping: in the 76px rail the group heading is `display: none`, so a folded
                  group would hide its items under a control that is not on screen. CSS carries the
                  width half, so this only has to carry the fold.
                */}
                <ul
                  className={open || !full ? "shell__items" : "shell__items shell__items--folded"}
                >
                  {section.pages.map(({ page: entry, access }) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        aria-current={entry.id === currentPageId ? "page" : undefined}
                        /*
                         * The design's own `title`, on every menu item, and it is load-bearing
                         * rather than a nicety: in the 76px rail the label is `display: none`,
                         * which takes it out of the accessibility tree as well as off the screen,
                         * so the title is all that names the button.
                         */
                        title={entry.railLabel ?? entry.title}
                        onClick={() => {
                          onNavigate(entry.id);
                          // A drawer that stayed open over the page it just opened would hide it.
                          setNavOpen(false);
                        }}
                      >
                        <span aria-hidden="true" className="shell__nav-glyph">
                          {entry.glyph}
                        </span>
                        <span className="shell__nav-label">
                          {entry.railLabel ?? entry.title}
                          {access === "read_only" ? (
                            // Said in navigation rather than discovered when a button is missing.
                            <span className="shell__readonly"> · view only</span>
                          ) : entry.blockedBy === undefined ? null : (
                            /*
                             * **The one rail affordance this console has and its siblings do not**,
                             * and it covers fifteen of sixteen items rather than a corner case.
                             *
                             * A page the platform cannot serve is kept in the rail — `resolvePage`
                             * returns it visible — because a platform merchant who could see one
                             * item of sixteen would conclude the console was broken rather than
                             * that the control plane is unserved. So the item is there, says it is
                             * not available in the same slot the design puts "view only" in, and
                             * opens the readiness page with its measured reason on it.
                             *
                             * Not a word the design file contains: its own rail has no such state,
                             * because it draws sixteen working pages. Recorded as **Q-182**. The
                             * alternative readings are hiding the fifteen, which is worse, and
                             * inventing a state badge for them, which is worse still.
                             */
                            <span className="shell__readonly"> · not available</span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}

        {/*
          What this administrator cannot see, counted rather than hidden — and on this console it
          has a second job. Eleven of the design's fifteen pages are blocked by the platform rather
          than by a role, and those are deliberately **not** counted here: an administrator told
          their role is short of eleven pages would ask for permissions that would change nothing.
          `pagesNotInRole` excludes them, which is why this count is usually zero and why
          `blockedPages()` is reported on the readiness page instead.
        */}
        {hidden.length === 0 ? null : (
          <details className="shell__hidden">
            <summary>
              <span aria-hidden="true" className="shell__hidden-glyph">
                visibility_off
              </span>
              {hidden.length === 1
                ? "1 page not in your role"
                : `${hidden.length} pages not in your role`}
            </summary>
            <ul>
              {hidden.map((entry) => (
                <li key={entry.id}>
                  <span className="shell__hidden-title">{entry.title}</span>
                  {entry.needs.length === 0 ? null : (
                    <span className="shell__hidden-needs">{entry.needs.join(" + ")}</span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </nav>

      {/*
        The scrim under an open drawer, which this console did not have.

        `02 Console Shell v3` draws one — `showNavOverlay: drawer && navOpen` — and the drawer here
        opened over a page that still looked like the thing in front. Clicking it dismisses the
        drawer, which is the gesture a person tries first and which a drawer with no scrim gives
        them no way to make: the only ways out were choosing a page or finding the menu button
        again underneath the open rail.

        Found by the finance console's own shell check, which drove its drawer at 390 and read
        `scrim false against the design's true`; this console's shell is not driven by any pixel
        check, because `serveConsole` in the fidelity harness is hard-wired to the dispatch
        console. So it was reported here as likely rather than measured, and it was here. The jsdom
        test below drives open → scrim → click → closed, which is the behaviour; the geometry is
        recorded as unmeasured in docs/OPEN_WORK.md.

        Inert to assistive technology rather than described: it is a dismiss affordance for a
        pointer, and the drawer itself is a labelled region a screen-reader user leaves by other
        means. A scrim announced as a button is a control with no name.
      */}
      {navOpen && drawerWidth ? (
        <div
          className="shell__scrim"
          aria-hidden="true"
          onClick={() => {
            setNavOpen(false);
          }}
        />
      ) : null}

      <main className="shell__main">
        <HeaderKit
          session={session}
          helpTitle={page?.title ?? "Merchant"}
          /* The design's first tip is the page's own description, so the page supplies it. */
          tips={page === undefined ? SHELL_TIPS : [page.purpose, ...SHELL_TIPS]}
          transport={transport}
          navOpen={navOpen}
          {...(onSignOut === undefined ? {} : { onSignOut })}
          onToggleNav={() => {
            setNavOpen((open) => !open);
          }}
        />

        {page === undefined ? (
          <div className="state state--empty">
            <p className="state__headline">No such page</p>
          </div>
        ) : !reachable && page.blockedBy === undefined ? (
          /*
           * A role refusal, which is the only thing this branch is for on this console.
           *
           * The tenant admin console's version of it also catches a page the *platform* cannot
           * serve, and that would be wrong here for fifteen of sixteen pages: "You do not have
           * access to Replay control" sends a merchant to whoever administers their roles, and
           * nobody there can grant a scope for an operation that is `/internal/v1` only. So a
           * blocked page falls through to `children`, which is the readiness page — the same
           * arrangement the other consoles use, reached for a different reason and reached far more
           * often.
           */
          <div className="state state--forbidden">
            <p className="state__headline">You do not have access to {page.title}</p>
            <p className="state__detail">Ask whoever administers your roles for access.</p>
          </div>
        ) : (
          children
        )}
      </main>
    </div>
  );
}
